'use client';
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';
import { cva, cx, type VariantProps } from 'class-variance-authority';
import styles from './toast.module.scss';
import { CheckmarkCircle, Close, Error, Info, Warning } from '../../icons';
import { JSX } from 'react';

export const colorSchemeVariants = {
  info: styles.info,
  success: styles.success,
  warning: styles.warning,
  error: styles.error
};

const toastClass = cva(styles.toast, {
  variants: {
    colorScheme: colorSchemeVariants
  },
  defaultVariants: {
    colorScheme: 'info'
  }
});

type ToastProps = {
  id: string | number;
  title: string;
  description?: string | React.ReactNode;
} & VariantProps<typeof toastClass>;

const colorSchemeToIcon: Record<keyof typeof colorSchemeVariants, JSX.Element> =
  {
    info: <Info />,
    success: <CheckmarkCircle />,
    warning: <Warning />,
    error: <Error />
  };

export function Toast(props: ToastProps) {
  const { title, description, id, colorScheme = 'info' } = props;

  return (
    <div className={toastClass({ colorScheme })}>
      <div className={styles.icon}>
        {colorSchemeToIcon[colorScheme as keyof typeof colorSchemeVariants]}
      </div>
      <div
        className={cx(styles.textContent, !description && styles.noDescription)}
      >
        <h1 className={styles.title}>{title}</h1>
        {description && <p className={styles.description}>{description}</p>}
      </div>
      <button
        className={styles.closeButton}
        aria-label="Close"
        onClick={() => {
          sonnerToast.dismiss(id);
        }}
      >
        <Close className={styles.closeIcon} />
      </button>
    </div>
  );
}

function toast(toast: Omit<ToastProps, 'id'>) {
  return sonnerToast.custom((id) => (
    <Toast
      id={id}
      title={toast.title}
      description={toast.description}
      colorScheme={toast.colorScheme}
    />
  ));
}

function Toaster() {
  return (
    <SonnerToaster
      offset={{
        right: '3rem',
        bottom: '3rem'
      }}
      mobileOffset={{
        bottom: '3rem'
      }}
      position="bottom-right"
    />
  );
}

const dismissToast = sonnerToast.dismiss;

export { Toaster, toast, dismissToast };
